import $ from 'jquery';
import Flickity from 'flickity';
import imagesLoaded from 'imagesloaded';
import debounce from 'just-debounce';
import CurrencyConverter from 'shopify-currency-converter';

import VariantHelper from '../VariantHelper';
import Forms from '../Forms';
import layout from '../Layout';
import AddToCartFlyout from './AddToCartFlyout';
import scrollLeft from '../helpers/scrollLeft';
import {removeTrapFocus, trapFocus} from '../helpers/a11y';
import Video from './Video';

export default class ProductDetails {
  constructor(options) {
    this.$window = $(window);
    this.$formArea = options.$formArea;
    this.$gallery = options.$gallery;
    this.$details = options.$details;
    this.context = options.context;
    this.settings = options.settings;
    this.product = options.product;
    this.useHistory = options.useHistory;

    this.$viewport = null;
    this.gallery = null;
    this.variantHelper = null;

    // Product ATC Actions
    this._onATCOpen = this._onATCOpen.bind(this);
    this._onATCClose = this._onATCClose.bind(this);

    this.atcCallbacks = $.extend({
      onClose: this._onATCClose,
      onOpen: this._onATCOpen,
    }, options.atcCallbacks);

    // Gallery
    this.slides = '[data-product-image]';
    this.$slides = this.$gallery.find(this.slides);
    this.slider = '[data-product-slider]';
    this.$slider = this.$gallery.find(this.slider);
    this.$sliderPagination = this.$gallery.find('[data-product-slider-pagination]');
    this.$paginationTrigger = this.$gallery.find('[data-product-thumbnail]');
    this.$videoEl = this.$gallery.find('[data-video]');

    // Form
    this.$form = this.$formArea.find('[data-product-form]');
    this.$productAtcButton = this.$formArea.find('[data-product-atc]');
    this.$productVariants = this.$form.find('[data-variants]');
    this.$productOptions = this.$form.find('[data-product-option]');

    this.forms = new Forms(this.$form);

    this.variantFields = {
      $price: this.$details.find('[data-price]'),
      $priceMoney: this.$details.find('[data-price] .money'),
      $compareAtPrice: this.$details.find('[data-price-compare-at]'),
      $compareAtPriceMoney: this.$details.find('[data-price-compare-at] .money'),
      $badge: this.$details.find('[data-badge-sales]'),
      $badgeMoneySaved: this.$details.find('[data-price-money-saved]'),
      $badgePercentSaved: this.$details.find('[data-price-percent-saved]'),
    };

    // Re-initialise gallery when crossing breakpoints if needed
    this.updateGallery = this.updateGallery.bind(this);
    layout.onBreakpointChange(this.updateGallery);

    this._bindEvents();

    if (this.$slides.length) {
      this._initGallery();
    }

    if (this.$videoEl) {
      this.videoArray = [];

      for (let i = 0; i < this.$slides.length; i++) {
        let video = this.$slider.find(`${this.slides}:nth-of-type(${i + 1}) [data-video]`);

        if (video.length) {
          video = new Video(video[0]);
          this.videoArray.push(video);

          if (i === 0 && this.settings.enable_video_autoplay) {
            video.autoplay();
          }
        } else {
          this.videoArray.push(null);
        }
      }
    }

    this._initOptions();
  }

  unload() {
    this.$window.off('product-variant-switch.product-details');
    this.$form.off('.product-details');
    this.forms.unload();
    layout.offBreakpointChange(this.updateGallery);

    if (this.variantHelper) {
      this.variantHelper.unload();
    }
    this._unloadGallery();
  }

  updateGallery() {
    this._unloadGallery();
    this._initGallery();
  }

  _bindEvents() {
    this.$window.on('product-variant-switch.product-details', (event, data) => this._switchVariant(event, data));
    this.$form.on('submit.product-details', event => this._AddToCartFlyout(event));
  }

  _initOptions() {
    if (!this.$productVariants.length) return;

    this.variantHelper = new VariantHelper(
      this.product,
      this.$productVariants,
      this.$productOptions
    );
  }

  _switchVariant(event, data) {
    const variant = data.variant;
    const firstLoad = data.firstLoad;

    if (firstLoad) {
      return;
    }

    // Update main select
    this.$productVariants.val(data.variant.id);

    // Update gallery image
    if (variant.featured_image) {
      const $variantImage = this.$slider.find(`[data-product-image=${variant.featured_image.id}]`);

      if (this.gallery) {
        this.gallery.select($variantImage.attr('data-product-image-index'), false, false);
      }
    }

    // Update Variant information
    this._updatePrice(variant);
    this._updateBadge(variant);
    this._updateButton(variant);

    if (this.useHistory) {
      const url = `${ this.product.handle }?${$.param({ variant: variant.id })}`;
      history.replaceState({}, 'variant', url);
    }
  }

  _updatePrice(variant) {
    // Update compare at price
    const hasComparePrice = (
      !!variant.compare_at_price && variant.compare_at_price > variant.price
    );
    this.variantFields.$compareAtPrice.toggleClass('visible', hasComparePrice);
    this.variantFields.$compareAtPriceMoney.text(Shopify.formatMoney(variant.compare_at_price, this.settings.money_format));
    CurrencyConverter.update(this.variantFields.$compareAtPriceMoney[0]);

    // Update price
    this.variantFields.$priceMoney.text(Shopify.formatMoney(variant.price, this.settings.money_format));
    CurrencyConverter.update(this.variantFields.$priceMoney[0]);
  }

  _updateBadge(variant) {
    const priceSaved = variant.compare_at_price ? variant.compare_at_price - variant.price : 0;
    this.variantFields.$badge.toggle(!!priceSaved);

    if (this.variantFields.$badgeMoneySaved.length) {
      // Update badge if it shows money saved
      this.variantFields.$badgeMoneySaved.text(Shopify.formatMoney(priceSaved, this.settings.money_format));
      CurrencyConverter.update(this.variantFields.$badgeMoneySaved[0]);
    }

    if (this.variantFields.$badgePercentSaved.length) {
      // Update badge if it shows percentiles
      const percentileSaved = Math.round((priceSaved * 100) / variant.compare_at_price);
      this.variantFields.$badgePercentSaved.text(percentileSaved);
    }
  }

  _updateButton(variant) {
    if (variant.available) {
      this.$productAtcButton.text(this.context.product_available);
      this.$productAtcButton
        .removeClass('disabled')
        .prop('disabled', false);
    } else {
      this.$productAtcButton.text(this.context.product_unavailable);
      this.$productAtcButton
        .addClass('disabled')
        .prop('disabled', true);
    }
  }

  _AddToCartFlyout(event) {
    event.preventDefault();

    const formData = this.$form.serializeArray();

    const options = {
      $atcButton: this.$productAtcButton,
      settings: {
        moneyFormat: this.settings.money_format,
        cartRedirection: this.settings.cart_redirection,
      },
    };

    AddToCartFlyout.init(formData, options, this.atcCallbacks);
  }

  /**
   * Callback when AddToCartFlyout opens
   * Move keyboard focus into flyout
   *
   * @param {object} options - Options to be used
   * @param {jQuery} options.$container - Container to trap focus within
   * @param {jQuery} options.$elementToFocus - Element to be focused when focus leaves container
   * @private
   */
  _onATCOpen(options) {
    trapFocus({
      $container: options.$container,
      $elementToFocus: options.$elementToFocus,
      namespace: 'product-details-flyout',
    });
  }

  /**
   * Callback when AddToCartFlyout closes
   * Move keyboard focus back into grid item
   *
   * @private
   */
  _onATCClose() {
    removeTrapFocus({
      namespace: 'product-details-flyout',
    });

    this.$productAtcButton.focus();
  }

  _unloadGallery() {
    if (this.gallery) {
      this.$window.off('.gallery');
      this.$paginationTrigger.off('.gallery');
      this.$slider.off('.gallery');

      this.gallery.destroy();
      this.gallery = null;
      this.$slider.find(this.slides).attr('style', '');
    }
  }

  _initGallery() {
    const $selectedImage = this.$slider.find('.image--selected');

    // Options shared on Desktop and Mobile
    let flickityOptions = {
      autoPlay: 0,
      accessibility: true,
      cellAlign: 'left',
      cellSelector: this.slides,
      imagesLoaded: true,
      pageDots: false,
      prevNextButtons: false,
      setGallerySize: false,
      wrapAround: true,
    };

    // Mobile only options
    const optionsMobile = {
      draggable: true,
      friction: 0.8,
      selectedAttraction: 0.2,
    };

    // Desktop only options
    const optionsDesktop = {
      draggable: false,
    };

    if (layout.isBreakpoint('XS')){
      flickityOptions = $.extend({}, optionsMobile, flickityOptions);
    } else {
      flickityOptions = $.extend({}, optionsDesktop, flickityOptions);
    }

    // If a variant has an image pre-selected, start the slideshow at that image
    // If the slideshow has been re-initalised across a break point, slideshow restarts at same slide
    if ($selectedImage.length) {
      const initialIndex = parseInt($selectedImage.attr('data-product-image-index'), 10);

      flickityOptions = $.extend({}, { initialIndex }, flickityOptions);
    }

    imagesLoaded(this.$slider[0], () => {
      this.gallery = new Flickity(this.$slider[0], flickityOptions);
      this.$viewport = $(this.gallery.viewport);

      // Sets the Slider to the height of the first slide
      this._setSlideHeight();

      this._bindGallery();
    });
  }

  _bindGallery() {
    /*
     On slide change, find the new height of the slide and set it as the
     height of the slider
     */
    this.gallery.on('select', () => {
      this.$slider.trigger('heightUpdate.gallery');
      this._updateActiveSlide(this.gallery.selectedIndex);

      for (let i = 0; i < this.$slides.length; i++) {
        const video = this.$slider.find(`${this.slides}:nth-of-type(${i + 1}) [data-video]`);

        if (video.length) {
          this.videoArray[i].onPauseClick();
        }
      }

      if (this.videoArray[this.gallery.selectedIndex] && this.settings.enable_video_autoplay) {
        this.videoArray[this.gallery.selectedIndex].autoplay();
      }
    });

    this.$slider.on('heightUpdate.gallery', () => {
      this._setSlideHeight();
    });

    this.$paginationTrigger.on('click.gallery', (event) => {
      event.preventDefault();
      const $target = $(event.currentTarget);
      this.gallery.select($target.attr('data-product-thumbnail'), false, false);
    });

    this.$window.on('resize.gallery', debounce(() => {
      this.$slider.trigger('heightUpdate.gallery');
    }));
  }

  _setSlideHeight() {
    const height = $(this.gallery.selectedElement).height();

    this.$viewport.css({height});
  }

  /**
   * Adds a non-flickity active class to thumbnails, and main slideshow
   *  - Is used on re-init across breakpoints
   *  - Is used to show the active thumbnail in the pagination
   *
   * @param index
   * @private
   */
  _updateActiveSlide(index) {
    const $newSlide = this.$slider.find(`[data-product-image-index=${index}]`);
    const $newThumbnail = this.$sliderPagination.find(`[data-product-thumbnail=${index}]`);

    $newSlide
      .addClass('image--selected')
      .siblings('.image--selected')
      .removeClass('image--selected');

    $newThumbnail
      .addClass('thumbnail--selected')
      .siblings('.thumbnail--selected')
      .removeClass('thumbnail--selected');

    if (layout.isBreakpoint('XS')) {
      scrollLeft($newThumbnail);
    }
  }
}
